import { toRadian } from '@/util/math';

type PerspectiveDistanceParams = {
  fov: number;
  worldHeight: number;
  zoom: number;
};

type PerspectiveWorldHeightParams = {
  distance: number;
  fov: number;
  zoom: number;
};

type WorldHeightRangeParams = {
  fov: number;
  maxDistance: number;
  minDistance: number;
  zoom: number;
};

const isPositiveFinite = (value: number) => Number.isFinite(value) && value > 0;

const getHalfFovTangent = (fov: number) =>
  isPositiveFinite(fov) && fov < 180 ? Math.tan(toRadian(fov / 2)) : 0;

export const getOrthographicWorldHeight = ({
  viewportHeight,
  zoom,
}: {
  viewportHeight: number;
  zoom: number;
}) =>
  isPositiveFinite(viewportHeight) && isPositiveFinite(zoom)
    ? viewportHeight / zoom
    : 0;

export const getOrthographicZoom = ({
  viewportHeight,
  worldHeight,
}: {
  viewportHeight: number;
  worldHeight: number;
}) =>
  isPositiveFinite(viewportHeight) && isPositiveFinite(worldHeight)
    ? viewportHeight / worldHeight
    : 0;

export const getPerspectiveDistance = ({
  fov,
  worldHeight,
  zoom,
}: PerspectiveDistanceParams) => {
  const halfFovTangent = getHalfFovTangent(fov);
  if (
    !halfFovTangent ||
    !isPositiveFinite(worldHeight) ||
    !isPositiveFinite(zoom)
  ) {
    return 0;
  }

  return (worldHeight * zoom) / (2 * halfFovTangent);
};

export const getPerspectiveWorldHeight = ({
  distance,
  fov,
  zoom,
}: PerspectiveWorldHeightParams) => {
  const halfFovTangent = getHalfFovTangent(fov);
  if (
    !halfFovTangent ||
    !isPositiveFinite(distance) ||
    !isPositiveFinite(zoom)
  ) {
    return 0;
  }

  return (2 * distance * halfFovTangent) / zoom;
};

/**
 * Perspective 카메라가 controls 의 거리 제한 안에서 표현할 수 있는 worldHeight 범위입니다.
 * Orthographic 은 zoom 제한이 없으므로 이 범위를 공통 클램프 기준으로 사용합니다.
 */
export const getWorldHeightRange = ({
  fov,
  maxDistance,
  minDistance,
  zoom,
}: WorldHeightRangeParams) => {
  const first = getPerspectiveWorldHeight({ distance: minDistance, fov, zoom });
  const second = getPerspectiveWorldHeight({
    distance: maxDistance,
    fov,
    zoom,
  });

  return { max: Math.max(first, second), min: Math.min(first, second) };
};
